import debounce from 'lodash/debounce'
import { BetterToast } from './toast'
import { isProtectedAPIRoutePath } from './utils'

const redirectToLogin = debounce(() => {
  BetterToast.error('Your session has expired. Please log in again.')
  window.location.href = '/api/user/signout'
}, 500)

let unauthorizedAttemptsCount = 0
const SESSION_EXPIRED_MAX_ALLOWED_ATTEMPTS = 3

// Define the extended fetch wrapper interface
interface FetchWrapper {
  (url: string, options?: RequestInit): Promise<Response>;
  get: <T = any>(url: string, options?: RequestInit) => Promise<T>;
  post: <T = any>(url: string, body?: any, options?: RequestInit) => Promise<T>;
  put: <T = any>(url: string, body?: any, options?: RequestInit) => Promise<T>;
  delete: <T = any>(url: string, options?: RequestInit) => Promise<T>;
}

// Create the base fetchWrapper
const baseFetchWrapper = async (url: string, options: RequestInit = {}): Promise<Response> => {
  const response = await fetch(url, options)

  if (isProtectedAPIRoutePath(url)) {
    if (response.status === 403) {
      unauthorizedAttemptsCount++
      console.error('Unauthorized attempts count:', unauthorizedAttemptsCount)
      if (unauthorizedAttemptsCount >= SESSION_EXPIRED_MAX_ALLOWED_ATTEMPTS) {
        unauthorizedAttemptsCount = 0
        redirectToLogin()
        return response
      }
    } else if (response.status >= 200 && response.status < 300) {
      if (unauthorizedAttemptsCount != 0) {
        console.info(`Unauthorized attempts count reset from ${unauthorizedAttemptsCount} to 0`)
      }

      unauthorizedAttemptsCount = 0
    }
  }

  return response
}

// Extended fetchWrapper with utility methods
const handleResponse = async <T>(response: Response): Promise<T> => {
  if (!response.ok) {
    const errorText = await response.text();
    let errorMessage = 'An error occurred';

    try {
      const errorJson = JSON.parse(errorText);
      errorMessage = errorJson.detail || errorJson.message || errorText;
    } catch {
      errorMessage = errorText || errorMessage;
    }

    throw new Error(errorMessage);
  }

  // Check if the response is empty
  const text = await response.text();
  return text ? JSON.parse(text) as T : {} as T;
};

// Create the extended fetchWrapper with all the utility methods
export const fetchWrapper: FetchWrapper = Object.assign(
  baseFetchWrapper,
  {
    get: async <T>(url: string, options: RequestInit = {}): Promise<T> => {
      const response = await baseFetchWrapper(url, {
        ...options,
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...options.headers,
        },
      });
      return handleResponse<T>(response);
    },

    post: async <T>(url: string, body?: any, options: RequestInit = {}): Promise<T> => {
      const response = await baseFetchWrapper(url, {
        ...options,
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...options.headers,
        },
        body: body ? JSON.stringify(body) : undefined,
      });
      return handleResponse<T>(response);
    },

    put: async <T>(url: string, body?: any, options: RequestInit = {}): Promise<T> => {
      const response = await baseFetchWrapper(url, {
        ...options,
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...options.headers,
        },
        body: body ? JSON.stringify(body) : undefined,
      });
      return handleResponse<T>(response);
    },

    delete: async <T>(url: string, options: RequestInit = {}): Promise<T> => {
      const response = await baseFetchWrapper(url, {
        ...options,
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          ...options.headers,
        },
      });
      return handleResponse<T>(response);
    }
  }
);
